import React, {useContext, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {toast} from 'react-toastify';
import {Button, FormFeedback, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';
import {Link} from 'react-router-dom';
import {editOrderSettings, getOrderSettingsInfo, resetOrderToken} from '../../api/api';
import AuthContext from '../../context/AuthContext';
import handleUnauthorized401MutateError from '../../utils/handleUnauthorized401MutateError';
import Loader from '../../components/Loader';

function OrderSettingsTab() {
    const {refreshToken} = useContext(AuthContext);
    const [settings, setSettings] = useState({});
    const [settingId, setSettingId] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const queryClient = useQueryClient();

    const {refetch} = useQuery({
        queryKey: ['order-settings'],
        queryFn: () => getOrderSettingsInfo(),
        keepPreviousData: true,
        retry: false,
        onSuccess: (data) => {
            setSettingId(data.results[0].id);
            setSettings(data.results[0]);
        },
        onError: (err) => {
            handleUnauthorized401MutateError(err, refreshToken, refetch);
            if (err.response.status !== 401) {
                toast.error(`An error occurred! ${err?.response?.data?.detail}`);
            }
        }
    });

    const orderSettingMutation = useMutation(editOrderSettings, {
        onSuccess: () => {
            setFieldErrors({});
            toast.success('Settings updated successfully!');
            queryClient.invalidateQueries(['order-settings']);
        },
        onError: (err) => {
            const data = err?.response?.data;
            if (data && typeof data === 'object' && !data.detail) {
                const errors = {};
                Object.keys(data).forEach(key => {
                    errors[key] = Array.isArray(data[key]) ? data[key][0] : data[key];
                });
                setFieldErrors(errors);
                const firstError = Object.values(errors)[0];
                toast.error(firstError);
            } else {
                toast.error(`An error occurred! ${data?.detail || 'Unknown error'}`);
            }
        },
    });

    const [resetTokenModalOpen, setResetTokenModalOpen] = useState(false);
    const toggleResetTokenModal = () => setResetTokenModalOpen((prev) => !prev);

    const resetTokenMutation = useMutation(resetOrderToken, {
        onSuccess: (data) => {
            // Merge only the token counters so unsaved edits in this form are kept
            setSettings((prev) => ({
                ...prev,
                last_token_number: data.last_token_number,
                last_token_date: data.last_token_date,
                next_token_number: data.next_token_number,
                is_token_reset: data.is_token_reset,
            }));
            setResetTokenModalOpen(false);
            toast.success(`Token reset. The next order gets token ${data.next_token_number}.`);
        },
        onError: (err) => {
            handleUnauthorized401MutateError(err, refreshToken, resetTokenMutation, settingId);
            if (err.response?.status !== 401) {
                setResetTokenModalOpen(false);
                // e.g. already reset by another user/tab: refresh the token status shown here
                queryClient.invalidateQueries(['order-settings']);
                toast.error(err?.response?.data?.detail || 'Could not reset token.');
            }
        },
    });

    const handleOrderSettingsChange = (e) => {
        const {name, value} = e.target;
        setSettings((prevSettings) => ({
            ...prevSettings,
            [name]: value,
        }));
        if (fieldErrors[name]) {
            setFieldErrors((prev) => ({...prev, [name]: undefined}));
        }
    };

    const handleOrderSettingsSubmit = (e) => {
        e.preventDefault();
        orderSettingMutation.mutate({formData: settings, settingId});
    };

    const orderNumberPreview = settings.order_number_prefix
        ? `${settings.order_number_prefix}-${settings.next_order_number || 1001}`
        : `${settings.next_order_number || 1001}`;

    return (
        <form onSubmit={handleOrderSettingsSubmit}>
            <div className="row">
                <div className="settings-card ml-3">
                    <div className="card-box">
                        <h6 className="text-muted mb-3" style={{textTransform: 'uppercase', fontSize: 11, letterSpacing: 1}}>Order Number</h6>
                        <div className="form-group">
                            <Label>Order Number Prefix</Label>
                            <Input
                                type="text"
                                name="order_number_prefix"
                                className="form-control"
                                value={settings.order_number_prefix || ''}
                                onChange={handleOrderSettingsChange}
                                placeholder="e.g. BLX"
                                maxLength={10}
                                invalid={!!fieldErrors.order_number_prefix}
                            />
                            {fieldErrors.order_number_prefix
                                ? <FormFeedback>{fieldErrors.order_number_prefix}</FormFeedback>
                                : <small className="text-muted">Leave blank for no prefix. Only letters and numbers allowed.</small>
                            }
                        </div>
                        <div className="form-group">
                            <Label>Next Order Number</Label>
                            <Input
                                type="text"
                                name="next_order_number"
                                className="form-control"
                                value={settings.next_order_number ?? ''}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === '' || /^\d+$/.test(val)) {
                                        handleOrderSettingsChange(e);
                                    }
                                }}
                                invalid={!!fieldErrors.next_order_number}
                            />
                            {fieldErrors.next_order_number
                                ? <FormFeedback>{fieldErrors.next_order_number}</FormFeedback>
                                : <small className="text-muted">The next order will use this number.</small>
                            }
                        </div>
                        <div className="form-group">
                            <Label>Preview</Label>
                            <div className="form-control" style={{backgroundColor: 'transparent', border: '1px solid #444'}}>
                                {orderNumberPreview}
                            </div>
                        </div>

                        <hr style={{borderColor: '#333', margin: '24px 0'}} />

                        <h6 className="text-muted mb-3" style={{textTransform: 'uppercase', fontSize: 11, letterSpacing: 1}}>Email Notifications</h6>
                        <div className="form-group">
                            <Label>Send Order Confirmation Email</Label>
                            <Input
                                type="select"
                                name="send_order_confirmation_email"
                                className="form-control"
                                value={settings.send_order_confirmation_email ?? false}
                                onChange={handleOrderSettingsChange}
                            >
                                <option value="true">YES</option>
                                <option value="false">NO</option>
                            </Input>
                            <small className="text-muted">Send an email to the customer when an order is placed.</small>
                        </div>
                        <div className="form-group">
                            <Label>Send Order Status Update Email</Label>
                            <Input
                                type="select"
                                name="send_order_status_update_email"
                                className="form-control"
                                value={settings.send_order_status_update_email ?? false}
                                onChange={handleOrderSettingsChange}
                            >
                                <option value="true">YES</option>
                                <option value="false">NO</option>
                            </Input>
                            <small className="text-muted">Send an email to the customer when order status changes.</small>
                        </div>

                        <hr style={{borderColor: '#333', margin: '24px 0'}} />

                        <h6 className="text-muted mb-3" style={{textTransform: 'uppercase', fontSize: 11, letterSpacing: 1}}>Short Bill &amp; Token</h6>
                        <div className="form-group">
                            <Label>Enable Token Number</Label>
                            <Input
                                type="select"
                                name="enable_token_number"
                                className="form-control"
                                value={settings.enable_token_number ?? false}
                                onChange={handleOrderSettingsChange}
                            >
                                <option value="true">YES</option>
                                <option value="false">NO</option>
                            </Input>
                            <small className="text-muted">Give every order a token number that restarts each day. It is printed large on the short bill so the customer can use it to collect their order.</small>
                        </div>
                        <div className="form-group">
                            <Label>Token Start Number</Label>
                            <Input
                                type="text"
                                name="token_start_number"
                                className="form-control"
                                value={settings.token_start_number ?? ''}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === '' || /^\d+$/.test(val)) {
                                        handleOrderSettingsChange(e);
                                    }
                                }}
                                invalid={!!fieldErrors.token_start_number}
                            />
                            {fieldErrors.token_start_number
                                ? <FormFeedback>{fieldErrors.token_start_number}</FormFeedback>
                                : <small className="text-muted">The first token of each day, and after a manual reset.</small>
                            }
                        </div>
                        <div className="form-group">
                            <Label>Token Resets At</Label>
                            <Input
                                type="time"
                                name="token_reset_time"
                                className="form-control"
                                value={(settings.token_reset_time || '04:00').slice(0, 5)}
                                onChange={handleOrderSettingsChange}
                                invalid={!!fieldErrors.token_reset_time}
                            />
                            {fieldErrors.token_reset_time
                                ? <FormFeedback>{fieldErrors.token_reset_time}</FormFeedback>
                                : <small className="text-muted">
                                    Tokens restart at this time every day. Pick a time after you close — e.g. if you
                                    close at 1 AM, 4 AM keeps late-night orders counting on from the evening.
                                </small>
                            }
                        </div>
                        <div className="form-group">
                            <Label>Current Token</Label>
                            <div className="d-flex align-items-center" style={{gap: 12}}>
                                <span>
                                    Next order gets token {settings.next_token_number ?? settings.token_start_number ?? 1}
                                    {!settings.is_token_reset && settings.last_token_date &&
                                        ` (last issued: ${settings.last_token_number}, business day ${settings.last_token_date})`}
                                </span>
                                <Button
                                    type="button"
                                    className="btn bx-btn-primary waves-effect waves-light text-nowrap"
                                    onClick={toggleResetTokenModal}
                                    disabled={!settingId || settings.is_token_reset || resetTokenMutation.isLoading}
                                >
                                    Reset Token
                                </Button>
                            </div>
                            {settings.is_token_reset && (
                                <small className="text-muted">
                                    Token is already reset. Tokens will start from {settings.token_start_number ?? 1} on the next order.
                                </small>
                            )}
                        </div>
                        <Modal isOpen={resetTokenModalOpen} toggle={toggleResetTokenModal}>
                            <ModalHeader toggle={toggleResetTokenModal} close={
                                <button type="button" className="close" onClick={toggleResetTokenModal}>
                                    &times;
                                </button>
                            }>
                                Reset Token?
                            </ModalHeader>
                            <ModalBody>
                                The next order will get token {settings.token_start_number ?? 1}, and tokens will count up
                                from there. Tokens already issued are not changed, so make sure all earlier tokens have
                                been served to avoid two orders with the same number.
                            </ModalBody>
                            <ModalFooter>
                                <Button
                                    type="button"
                                    color="primary"
                                    onClick={() => resetTokenMutation.mutate(settingId)}
                                    disabled={resetTokenMutation.isLoading}
                                >
                                    {resetTokenMutation.isLoading ? <><Loader size={'small'}/>Resetting...</> : 'Reset Token'}
                                </Button>
                                <Button type="button" color="secondary" onClick={toggleResetTokenModal}>Cancel</Button>
                            </ModalFooter>
                        </Modal>
                        <div className="form-group">
                            <Label>Paper Width</Label>
                            <Input
                                type="select"
                                name="short_bill_paper_width"
                                className="form-control"
                                value={settings.short_bill_paper_width || '80'}
                                onChange={handleOrderSettingsChange}
                            >
                                <option value="80">80mm</option>
                                <option value="58">58mm</option>
                            </Input>
                            <small className="text-muted">
                                Width of your thermal printer roll. When printing, set Margins to <b>Minimum</b> under
                                More settings in Chrome's print dialog, or blank paper is left above the bill.
                            </small>
                        </div>
                        <div className="form-group">
                            <Label>Print Width (mm)</Label>
                            <Input
                                type="text"
                                name="short_bill_print_width_mm"
                                className="form-control"
                                value={settings.short_bill_print_width_mm ?? ''}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === '' || /^\d+$/.test(val)) {
                                        setSettings((prev) => ({...prev, short_bill_print_width_mm: val === '' ? null : val}));
                                        setFieldErrors((prev) => ({...prev, short_bill_print_width_mm: undefined}));
                                    }
                                }}
                                placeholder={settings.short_bill_paper_width === '58' ? '48 (default)' : '72 (default)'}
                                invalid={!!fieldErrors.short_bill_print_width_mm}
                            />
                            {fieldErrors.short_bill_print_width_mm
                                ? <FormFeedback>{fieldErrors.short_bill_print_width_mm}</FormFeedback>
                                : <small className="text-muted">
                                    If the right edge of the bill is cut off, print the{' '}
                                    <Link to={`/sell/short-bill-alignment-test?paper=${settings.short_bill_paper_width || '80'}`}>
                                        alignment test
                                    </Link>{' '}
                                    and enter the last number you can fully read. Leave blank for the default.
                                </small>
                            }
                        </div>
                        <div className="form-group">
                            <Label>Print Short Bill After Placing Order</Label>
                            <Input
                                type="select"
                                name="auto_print_short_bill"
                                className="form-control"
                                value={settings.auto_print_short_bill ?? false}
                                onChange={handleOrderSettingsChange}
                            >
                                <option value="true">YES</option>
                                <option value="false">NO</option>
                            </Input>
                            <small className="text-muted">Open the print dialog as soon as an order is placed from the cart.</small>
                        </div>
                        <div className="form-group">
                            <Label>Short Bill Footer</Label>
                            <Input
                                type="text"
                                name="short_bill_footer"
                                className="form-control"
                                value={settings.short_bill_footer || ''}
                                onChange={handleOrderSettingsChange}
                                placeholder="e.g. Thank you! Visit again."
                                maxLength={120}
                            />
                        </div>

                        <hr style={{borderColor: '#333', margin: '24px 0'}} />

                        <h6 className="text-muted mb-3" style={{textTransform: 'uppercase', fontSize: 11, letterSpacing: 1}}>Other</h6>
                        <div className="form-group">
                            <Label>Allow Multiple Pending Orders</Label>
                            <Input
                                type="select"
                                name="allow_multiple_pending_orders"
                                className="form-control"
                                value={settings.allow_multiple_pending_orders ?? false}
                                onChange={handleOrderSettingsChange}
                            >
                                <option value="true">YES</option>
                                <option value="false">NO</option>
                            </Input>
                        </div>

                        <div className="form-group">
                            <Label>Default Payment Method</Label>
                            <Input
                                type="select"
                                name="default_payment_method"
                                className="form-control"
                                value={settings.default_payment_method || 'CASH'}
                                onChange={handleOrderSettingsChange}
                            >
                                <option value="CASH">Cash</option>
                                <option value="RAZORPAY">Razorpay</option>
                            </Input>
                            <small className="text-muted">Pre-selected payment method when placing orders from the cart.</small>
                        </div>

                        <Button type="submit" className="btn bx-btn-primary waves-effect waves-light" disabled={orderSettingMutation.isLoading || !settingId}>
                            {orderSettingMutation.isLoading ? <><Loader size={'small'}/>Saving...</> : 'Save'}
                        </Button>
                    </div>
                </div>
            </div>
        </form>
    );
}

export default OrderSettingsTab;
